import React, { useState, useEffect, useMemo } from 'react';
import { X, Folder, Trash2, Plus, FolderOpen, Search, LayoutList, Table2 } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';

interface DesignListSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type DesignsViewMode = 'tiles' | 'list';
const DESIGNS_VIEW_KEY = 'netbuilder-designs-view';

function readDesignsView(): DesignsViewMode {
  try {
    const raw = localStorage.getItem(DESIGNS_VIEW_KEY);
    if (raw === 'list' || raw === 'tiles') return raw;
  } catch {
    /* ignore */
  }
  return 'tiles';
}

const DesignListSidebar: React.FC<DesignListSidebarProps> = ({ isOpen, onClose }) => {
  const [topOffset, setTopOffset] = useState(64);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingDesignId, setEditingDesignId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [viewMode, setViewMode] = useState<DesignsViewMode>(readDesignsView);

  const {
    currentProject,
    getCurrentDesign,
    getDesignsForCurrentProject,
    renameDesign,
    deleteDesign,
    openDesignTab,
    openDesigns,
  } = useProjectStore();

  const currentDesign = getCurrentDesign();
  const designs = getDesignsForCurrentProject();

  const setDesignsView = (mode: DesignsViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(DESIGNS_VIEW_KEY, mode);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const calculateTopOffset = () => {
      const header = document.querySelector('header');
      const tabBar = document.querySelector('[data-tab-bar="true"]');
      let offset = 0;
      if (header) {
        offset += header.getBoundingClientRect().height;
      }
      if (tabBar) {
        offset += tabBar.getBoundingClientRect().height;
      }
      setTopOffset(offset || 64);
    };

    calculateTopOffset();
    window.addEventListener('resize', calculateTopOffset);

    return () => {
      window.removeEventListener('resize', calculateTopOffset);
    };
  }, [isOpen]);

  const filteredDesigns = useMemo(() => {
    if (!searchQuery.trim()) {
      return designs;
    }
    const query = searchQuery.toLowerCase();
    return designs.filter((d) => d.name?.toLowerCase().includes(query));
  }, [designs, searchQuery]);

  const handleLoadDesign = (designId: string) => {
    if (!currentProject) return;
    openDesignTab(designId);
    onClose();
  };

  const handleRenameStart = (
    designId: string,
    currentName: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setEditingDesignId(designId);
    setEditingName(currentName || '');
  };

  const handleRenameSave = () => {
    if (!editingDesignId || !editingName.trim()) {
      setEditingDesignId(null);
      setEditingName('');
      return;
    }
    renameDesign(editingDesignId, editingName.trim());
    setEditingDesignId(null);
    setEditingName('');
  };

  const handleRenameCancel = () => {
    setEditingDesignId(null);
    setEditingName('');
  };

  const handleDeleteDesign = (designId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDeleteId(designId);
  };

  const handleCreateDesign = () => {
    window.dispatchEvent(new CustomEvent('openDesignModal'));
  };

  const renderName = (design: (typeof designs)[number], className: string) => {
    if (editingDesignId === design.id) {
      return (
        <input
          type="text"
          value={editingName}
          onChange={(e) => setEditingName(e.target.value)}
          onBlur={handleRenameSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleRenameSave();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              handleRenameCancel();
            }
          }}
          className="font-semibold text-white bg-slate-700 border border-blue-500 rounded px-2 py-1 flex-1 min-w-0 outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
    return (
      <h3
        className={className}
        onClick={(e) => handleRenameStart(design.id, design.name, e)}
        title="Click to rename"
      >
        {design.name || 'Untitled Design'}
      </h3>
    );
  };

  return (
    <div
      className={`fixed left-12 bottom-0 w-80 bg-slate-900 text-white border-r border-slate-700 flex flex-col transition-transform duration-300 ease-in-out z-[60] ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
      style={{ top: `${topOffset}px` }}
    >
      <div className="p-4 border-b border-slate-700 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-blue-400 flex items-center">
            <FolderOpen size={18} className="mr-2 flex-shrink-0" />
            Designs
          </h2>
          {currentProject && (
            <p className="text-xs text-slate-400 mt-1 truncate">
              Collection:&nbsp;
              <span className="font-semibold">
                {currentProject.name || 'Untitled Collection'}
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div
            className="inline-flex items-center rounded border overflow-hidden"
            style={{ borderColor: '#334155' }}
            title="How to show designs"
          >
            <button
              type="button"
              onClick={() => setDesignsView('tiles')}
              className="inline-flex items-center justify-center h-7 w-7 transition-colors"
              style={{
                backgroundColor: viewMode === 'tiles' ? '#1d2535' : 'transparent',
                color: viewMode === 'tiles' ? '#60a5fa' : '#94a3b8',
              }}
              title="Tiles"
            >
              <Table2 size={12} />
            </button>
            <button
              type="button"
              onClick={() => setDesignsView('list')}
              className="inline-flex items-center justify-center h-7 w-7 transition-colors"
              style={{
                backgroundColor: viewMode === 'list' ? '#1d2535' : 'transparent',
                color: viewMode === 'list' ? '#60a5fa' : '#94a3b8',
                borderLeft: '1px solid #334155',
              }}
              title="List"
            >
              <LayoutList size={12} />
            </button>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-800 rounded transition-colors"
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
        {designs.length > 0 && (
          <div className="relative mb-4">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search designs in this collection..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-md text-sm text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}

        <button
          onClick={handleCreateDesign}
          className="w-full flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!currentProject}
        >
          <Plus size={16} />
          <span>New Design</span>
        </button>

        {!currentProject ? (
          <div className="text-center py-12">
            <Folder size={48} className="mx-auto text-gray-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              No Collection Open
            </h3>
            <p className="text-gray-400 mb-4">
              Open or create a collection to manage its designs.
            </p>
          </div>
        ) : designs.length === 0 ? (
          <div className="text-center py-12">
            <Folder size={48} className="mx-auto text-gray-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              No Designs in this Collection
            </h3>
            <p className="text-gray-400 mb-4">
              Create your first design in this collection to get started.
            </p>
            <button
              onClick={handleCreateDesign}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors"
              disabled={!currentProject}
            >
              Create New Design
            </button>
          </div>
        ) : filteredDesigns.length === 0 ? (
          <div className="text-center py-8">
            <Search size={32} className="mx-auto text-gray-500 mb-2" />
            <p className="text-sm text-gray-400">
              No designs found matching &quot;{searchQuery}&quot;
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="flex flex-col divide-y divide-slate-700/80 rounded border border-slate-700 overflow-hidden">
            {filteredDesigns.map((design) => {
              const isSelected = currentDesign?.id === design.id;
              const isOpened = isSelected || openDesigns.includes(design.id);
              return (
                <div
                  key={design.id}
                  onClick={() => handleLoadDesign(design.id)}
                  className={`group flex items-center gap-2 px-2 py-1.5 cursor-pointer ${
                    isSelected ? 'bg-blue-900/30' : 'hover:bg-slate-800'
                  }`}
                >
                  <FolderOpen className="text-blue-400 flex-shrink-0" size={14} />
                  <div className="flex-1 min-w-0">
                    {renderName(
                      design,
                      'text-sm font-medium text-white truncate cursor-pointer hover:text-blue-400 transition-colors'
                    )}
                    <div className="text-[11px] text-slate-400 truncate">
                      {design.equipment.length} eq · {design.cables.length} cables · {design.racks.length} racks
                    </div>
                  </div>
                  {isOpened && (
                    <span className="flex-shrink-0 text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded">
                      Opened
                    </span>
                  )}
                  <button
                    onClick={(e) => handleDeleteDesign(design.id, e)}
                    className="p-1 hover:bg-red-600 rounded transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                    title="Delete Design"
                  >
                    <Trash2 size={12} className="text-red-400" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredDesigns.map((design) => {
              const isSelected = currentDesign?.id === design.id;
              const isOpened = isSelected || openDesigns.includes(design.id);

              return (
                <div
                  key={design.id}
                  onClick={() => handleLoadDesign(design.id)}
                  className={`bg-slate-800 rounded-lg p-3 cursor-pointer transition-all hover:bg-slate-700 border-2 group ${
                    isSelected
                      ? 'border-blue-500 bg-blue-900/30'
                      : isOpened
                        ? 'border-slate-600'
                        : 'border-transparent'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <FolderOpen className="text-blue-400 flex-shrink-0" size={16} />
                      {renderName(
                        design,
                        'font-semibold text-white truncate cursor-pointer hover:text-blue-400 transition-colors'
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => handleDeleteDesign(design.id, e)}
                        className="p-1 hover:bg-red-600 rounded transition-colors flex-shrink-0"
                        title="Delete Design"
                      >
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1 text-xs text-gray-400">
                    {currentProject?.customer?.name && (
                      <div className="truncate">{currentProject.customer.name}</div>
                    )}
                    <div className="flex items-center space-x-3">
                      <span>{design.equipment.length} equipment</span>
                      <span>{design.cables.length} cables</span>
                      <span>{design.racks.length} racks</span>
                    </div>
                  </div>

                  {isOpened && (
                    <div className="mt-2">
                      <div className="inline-block text-xs bg-blue-600 text-white px-2 py-1 rounded">
                        Opened
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirmDeleteId && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center"
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            className="bg-slate-900 text-white rounded-lg shadow-2xl w-[400px] max-w-[92vw] border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-700 font-semibold text-sm flex items-center justify-between">
              <span>Delete Design</span>
              <button
                className="p-1 hover:bg-slate-800 rounded transition-colors"
                onClick={() => setConfirmDeleteId(null)}
              >
                <X size={16} className="text-gray-400" />
              </button>
            </div>
            <div className="p-4 text-sm text-gray-300">
              Are you sure you want to delete this design? This action cannot be
              undone.
            </div>
            <div className="px-4 py-3 border-t border-slate-700 bg-slate-900 flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm rounded bg-red-600 hover:bg-red-700 text-white transition-colors"
                onClick={() => {
                  if (confirmDeleteId) {
                    deleteDesign(confirmDeleteId);
                    setConfirmDeleteId(null);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignListSidebar;
